import type { GoldZoneSettings, InvestmentRecord, PortfolioPosition } from "../types/domain";

const today = new Date().toISOString().slice(0, 10);

const companyRows = [
  ["asur-b", "ASUR B", "Grupo Aeroportuario del Sureste", "BMV", "MXN", "Industrials", "Airports", "Mexico", 505, 505, 720, 0.162, 0.299, 0.027, 88, 90, "Gold", "Strong Buy", 16800, "Low"],
  ["walmex", "WALMEX", "Wal-Mart de Mexico", "BMV", "MXN", "Consumer Staples", "Retail", "Mexico", 63.5, 63.5, 76, 0.118, 0.164, 0.021, 82, 73, "Watch", "Hold", 48200, "Low"],
  ["kof-ubl", "KOF UBL", "Coca-Cola FEMSA", "BMV", "MXN", "Consumer Staples", "Beverages", "Mexico", 156.2, 156.2, 205, 0.147, 0.238, 0.034, 86, 84, "Silver", "Buy", 25500, "Medium"],
  ["femsa-ubd", "FEMSA UBD", "Fomento Economico Mexicano", "BMV", "MXN", "Consumer Staples", "Conglomerate", "Mexico", 184.1, 184.1, 215, 0.104, 0.144, 0.018, 79, 70, "Fair Value", "Hold", 39800, "Medium"],
  ["gmxt", "GMXT", "GMexico Transportes", "BMV", "MXN", "Industrials", "Railroads", "Mexico", 39.4, 39.4, 55, 0.151, 0.284, 0.016, 84, 86, "Silver", "Buy", 11800, "Medium"],
  ["googl", "GOOGL", "Alphabet Inc.", "SIC", "USD", "Communication Services", "Internet Services", "United States", 178, 3382, 238, 0.154, 0.252, 0, 91, 88, "Gold", "Strong Buy", 72000, "Low"],
  ["msft", "MSFT", "Microsoft Corporation", "SIC", "USD", "Technology", "Software", "United States", 424, 8056, 510, 0.126, 0.169, 0.008, 94, 79, "Watch", "Buy", 82500, "Low"],
  ["brk-b", "BRK.B", "Berkshire Hathaway", "SIC", "USD", "Financials", "Diversified Holding Company", "United States", 412, 7828, 480, 0.108, 0.142, 0, 89, 72, "Fair Value", "Hold", 54200, "Low"],
  ["cmcsa", "CMCSA", "Comcast Corporation", "SIC", "USD", "Communication Services", "Media", "United States", 38, 722, 52, 0.145, 0.269, 0.033, 76, 82, "Silver", "Buy", 13600, "High"],
  ["ibm", "IBM", "International Business Machines", "SIC", "USD", "Technology", "IT Services", "United States", 186, 3534, 176, 0.074, -0.057, 0.036, 69, 58, "Avoid", "Wait", 10400, "High"]
] as const;

export const goldZoneSettings: GoldZoneSettings = {
  minimumBuffettScore: 85,
  minimumOpportunityScore: 85,
  minimumMarginOfSafety: 0.2,
  minimumExpectedCagr: 0.14,
  requirePositiveFcf: true,
  requireNoCriticalWarning: true
};

export const demoInvestments: InvestmentRecord[] = companyRows.map((row) => {
  const [
    slug,
    ticker,
    companyName,
    market,
    currency,
    sector,
    industry,
    country,
    price,
    priceMxn,
    intrinsicValue,
    expectedCagr,
    marginOfSafety,
    dividend,
    buffett,
    opportunity,
    zone,
    recommendation,
    fcf,
    riskRating
  ] = row;

  return {
    company: {
      id: slug,
      ticker,
      companyName,
      slug,
      market,
      exchange: market === "BMV" ? "BMV" : "SIC",
      nativeCurrency: currency,
      sector,
      industry,
      country,
      businessDescription: `${companyName} is included as fictional GBVI demonstration data. Values are sample-only, not live, and not investment advice.`
    },
    price: {
      price,
      currency,
      priceMxn,
      fxRate: currency === "USD" ? 19 : 1,
      priceDate: today,
      source: "GBVI sample seed - fictional values",
      dataStatus: "sample",
      gbmSicPrice: market === "SIC" ? priceMxn * 1.01 : null
    },
    financials: [2025, 2024, 2023, 2022, 2021].map((year, index) => {
      const scale = 1 - index * 0.055;
      const revenue = fcf * 6.2 * scale;
      const operatingIncome = fcf * 1.8 * scale;
      const netIncome = fcf * 1.35 * scale;
      const operatingCashFlow = fcf * 1.22 * scale;
      const capex = fcf * -0.22 * scale;
      return {
        fiscalYear: year,
        periodType: "annual",
        periodEndDate: `${year}-12-31`,
        currency,
        units: "millions",
        revenue,
        operatingIncome,
        netIncome,
        eps: 5.4 * scale,
        operatingCashFlow,
        capitalExpenditure: capex,
        freeCashFlow: operatingCashFlow + capex,
        cashAndEquivalents: fcf * 1.6 * scale,
        totalDebt: fcf * 2 * scale,
        shareholdersEquity: netIncome / 0.18,
        sharesOutstanding: 1000,
        dividendsPaid: fcf * -0.18 * scale,
        interestExpense: fcf * -0.08 * scale,
        currentAssets: fcf * 2.2 * scale,
        currentLiabilities: fcf * 1.1 * scale,
        inventory: fcf * 0.18 * scale,
        source: "GBVI sample seed - fictional values",
        dataStatus: "sample"
      };
    }),
    valuation: {
      conservativeValue: intrinsicValue * 0.85,
      baseValue: intrinsicValue,
      optimisticValue: intrinsicValue * 1.18,
      strongBuyPrice: intrinsicValue * 0.7,
      buyPrice: intrinsicValue * 0.85,
      fairValueLow: intrinsicValue * 0.95,
      fairValueHigh: intrinsicValue * 1.05,
      takeProfitPrice: intrinsicValue * 1.25,
      expectedFiveYearPrice: intrinsicValue * Math.pow(1 + expectedCagr, 5),
      expectedFiveYearCagr: expectedCagr,
      marginOfSafety,
      modelVersion: "gbvi-mvp-v1",
      calculationDate: today,
      dataStatus: "sample"
    },
    quality: {
      businessQualityScore: 17,
      financialStrengthScore: 17,
      valuationScore: 16,
      growthScore: 13,
      managementScore: 8,
      dividendScore: dividend > 0 ? 7 : 3,
      riskScore: 4,
      buffettScore: buffett,
      opportunityScore: opportunity,
      moatNotes: "Sample moat assessment for interface development. Requires approved source analysis.",
      managementNotes: "Sample management quality note. Requires human review.",
      riskNotes: "Sample risk assessment. Validate against approved filings before publication.",
      dataStatus: "sample"
    },
    recommendation: {
      recommendation,
      zone,
      investmentThesis: "Sample thesis for development only. Do not use this as financial advice.",
      reasonsToBuy: ["Sample margin of safety is attractive", "Sample cash generation supports the valuation"],
      reasonsNotToBuy: ["Inputs are fictional", "Requires approved source documents before production use"],
      confidence: 0.7,
      unresolvedCriticalWarning: false,
      dataStatus: "sample",
      updatedAt: today
    },
    dividendYield: dividend,
    dividendSafety: dividend > 0.025 ? "High" : dividend > 0 ? "Medium" : "Unavailable",
    riskRating
  } satisfies InvestmentRecord;
});

export const demoPortfolio: PortfolioPosition[] = [
  { id: "p1", ticker: "ASUR B", companyId: "asur-b", quantity: 12, averageCost: 430, currency: "MXN", purchaseDate: "2025-04-15" },
  { id: "p2", ticker: "GOOGL", companyId: "googl", quantity: 5, averageCost: 151, currency: "USD", purchaseDate: "2025-09-03" },
  { id: "p3", ticker: "KOF UBL", companyId: "kof-ubl", quantity: 20, averageCost: 142, currency: "MXN", purchaseDate: "2026-01-10" },
  { id: "p4", ticker: "MSFT", companyId: "msft", quantity: 2, averageCost: 382, currency: "USD", purchaseDate: "2025-12-02" }
];
